import {
  NewPraksisstedSignatar,
  NewPraksisstedSignatarDraft,
  PraksisstedForAvtale,
  useAgreementContext,
} from '../index';
import {
  MinimalPraksisstedMedHierarkiDto,
  MinimalPraksisstedMedHierarkiNoderDto,
} from '../../../api/generated/studiested-generated-api';
import React, {
  FormEvent,
  Reducer,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import praksisstedTranslations from './praksisstedsection.i18n';
import {
  Table,
  TableCellTd,
  TableCellTh,
  TableHeader,
  TableMain,
  TableRow,
} from '../../../components/table/alpTable';
import * as css from './stedsection.module.scss';
import Button from '../../../components/button';
import { useSetTimeout } from '../../../hooks/useSetTimeout';
import { GenericActionWithPayload, validateOrgnr } from '../../../util';
import { StudiestedApiContext } from '../../../api/StudiestedApiContext';
import { useLoader } from '../../../api/useLoader';
import { isStrictlyNumeric } from '../../../util/stringutils';
import { FakeLockedInput, TextInput } from '../../../components/input';
import Spinner from '../../../components/spinner/spinner';
import Autocomplete from '../../../components/input/autocomplete';
import { IndentMuligPraksissted } from '../../../components/indentation/indentation';
import {
  ModalTable,
  TABLE_LOADER_HEIGHT,
  usePraksisstedToEditContext,
  useSelectedStedContext,
} from './index';
import PanelHeader from '../../../components/panelheader/panelHeader';
import { devEnvFlag, testEnvFlag } from '../../../util/featureFlags';

interface PraksisstedTableProps {
  steder: PraksisstedForAvtale[];
  setPraksisstedToEdit: (sted: PraksisstedForAvtale) => void;
  removePraksissted: (sted: PraksisstedForAvtale) => void;
}

enum PraksisstedTableHeader {
  STED = 'STED',
  AVDELING = 'AVDELING',
  SIGNATAR = 'SIGNATAR',
  HANDLING = 'HANDLING',
}

const getToppnode = (
  praksissted: MinimalPraksisstedMedHierarkiDto,
): MinimalPraksisstedMedHierarkiDto =>
  praksissted.noder.over ? getToppnode(praksissted.noder.over) : praksissted;

export const PraksisstedTable: React.FC<
  React.PropsWithChildren<PraksisstedTableProps>
> = ({ steder, setPraksisstedToEdit, removePraksissted }) => {
  const { canEditAgreement } = useAgreementContext();
  const { formatMessage, locale } = useIntl();

  const labels = useMemo(
    () => ({
      editSted: formatMessage({ id: praksisstedTranslations.editSted.id }),
      showSted: formatMessage({ id: praksisstedTranslations.showSted.id }),
      removeSted: formatMessage({ id: praksisstedTranslations.removeSted.id }),
    }),
    [formatMessage],
  );

  const headerKeys = useMemo(() => Object.keys(PraksisstedTableHeader), []);
  const initActiveCol = PraksisstedTableHeader.STED;

  const compare = Intl.Collator(locale || 'nb').compare;
  const sort = useCallback(
    (col: number, a: PraksisstedForAvtale, b: PraksisstedForAvtale) => {
      switch (col) {
        case headerKeys.indexOf(PraksisstedTableHeader.STED):
          return compare(getToppnode(a).navn, getToppnode(b).navn);
        case headerKeys.indexOf(PraksisstedTableHeader.AVDELING):
          return compare(a.navn, b.navn);
        case headerKeys.indexOf(PraksisstedTableHeader.SIGNATAR):
          return compare(
            a?.signatar?.etternavn || '',
            b?.signatar?.etternavn || '',
          );
      }
      return 0;
    },
    [compare, headerKeys],
  );

  const [activeCol, setActiveCol] = useState<number>(
    headerKeys.indexOf(initActiveCol),
  );
  const [ascending, setAscending] = useState<boolean>(false);
  const sortedPraksissteder = useMemo(
    () =>
      [...steder].sort((a, b) =>
        ascending ? sort(activeCol, b, a) : sort(activeCol, a, b),
      ),
    [ascending, activeCol, sort, steder],
  );

  const tableHeaders = useMemo(
    () =>
      devEnvFlag || testEnvFlag
        ? {
            [PraksisstedTableHeader.STED]: formatMessage({
              id: praksisstedTranslations.stedHeader.id,
            }),
            [PraksisstedTableHeader.AVDELING]: formatMessage({
              id: praksisstedTranslations.avdelingHeader.id,
            }),
            [PraksisstedTableHeader.SIGNATAR]: formatMessage({
              id: praksisstedTranslations.signatarHeader.id,
            }),
            [PraksisstedTableHeader.HANDLING]: formatMessage({
              id: praksisstedTranslations.handlingHeader.id,
            }),
          }
        : {
            [PraksisstedTableHeader.STED]: formatMessage({
              id: praksisstedTranslations.stedHeader.id,
            }),
            [PraksisstedTableHeader.AVDELING]: formatMessage({
              id: praksisstedTranslations.avdelingHeader.id,
            }),
            [PraksisstedTableHeader.HANDLING]: formatMessage({
              id: praksisstedTranslations.handlingHeader.id,
            }),
          },
    [formatMessage],
  );

  const headers = useMemo(
    () =>
      Object.entries(tableHeaders).map(([type, title]) => {
        const idx = headerKeys.indexOf(type);

        return (
          <TableCellTh
            key={`${title}-${type}`}
            colIndex={idx}
            activeCol={idx === activeCol}
            setActiveCol={setActiveCol}
            canSort={type !== PraksisstedTableHeader.HANDLING}
            sort={(col, asc) => {
              setActiveCol(col);
              setAscending(asc);
            }}
            align={
              type === PraksisstedTableHeader.HANDLING ? 'right' : undefined
            }
          >
            {title}
          </TableCellTh>
        );
      }),
    [tableHeaders, activeCol, headerKeys],
  );

  const rows = useMemo(
    () =>
      sortedPraksissteder.map((sted) => {
        const { signatar, navn, id } = sted;
        const signatarNavn = signatar
          ? `${signatar.fornavn} ${signatar.etternavn}`
          : '';

        return (
          <TableRow key={id}>
            <TableCellTd header={tableHeaders[PraksisstedTableHeader.STED]}>
              {getToppnode(sted).navn}
            </TableCellTd>
            <TableCellTd header={tableHeaders[PraksisstedTableHeader.AVDELING]}>
              {navn}
            </TableCellTd>
            {(devEnvFlag || testEnvFlag) && (
              <TableCellTd
                header={tableHeaders[PraksisstedTableHeader.SIGNATAR]!!}
              >
                {signatarNavn}
              </TableCellTd>
            )}
            <TableCellTd
              header={tableHeaders[PraksisstedTableHeader.HANDLING]}
              align={'right'}
            >
              {canEditAgreement ? (
                <div className={css.btnGroup}>
                  <Button.Tertiary
                    className={css.btn}
                    onClick={() => removePraksissted(sted)}
                    id={`remove-praksissted-${id}`}
                  >
                    {labels.removeSted}
                  </Button.Tertiary>
                  <Button.Quaternary
                    className={css.btn}
                    onClick={() => setPraksisstedToEdit(sted)}
                    id={`edit-praksissted-${id}`}
                  >
                    {labels.editSted}
                  </Button.Quaternary>
                </div>
              ) : (
                <Button.Secondary
                  className={css.btn}
                  onClick={() => setPraksisstedToEdit(sted)}
                >
                  {labels.showSted}
                </Button.Secondary>
              )}
            </TableCellTd>
          </TableRow>
        );
      }),
    [
      tableHeaders,
      sortedPraksissteder,
      labels,
      removePraksissted,
      setPraksisstedToEdit,
      canEditAgreement,
    ],
  );

  return (
    <Table>
      <TableHeader>
        <TableRow top>{headers}</TableRow>
      </TableHeader>
      <TableMain>{rows}</TableMain>
    </Table>
  );
};

type PraksisstedForSelect = {
  id: string;
  navn: string;
  dybde: number;
};

const mapUndernoderToSelectable = (
  noder: MinimalPraksisstedMedHierarkiNoderDto,
  dybde: number,
): PraksisstedForSelect[] =>
  noder.under?.reduce(
    (acc: PraksisstedForSelect[], sted: MinimalPraksisstedMedHierarkiDto) =>
      acc
        .concat({ id: sted.id, navn: sted.navn, dybde: dybde })
        .concat(mapUndernoderToSelectable(sted.noder, dybde + 1)),
    [],
  ) || [];

const mapToppnodeToSelectable = (
  praksissted: MinimalPraksisstedMedHierarkiDto,
): PraksisstedForSelect[] =>
  [{ id: praksissted.id, navn: praksissted.navn, dybde: 0 }].concat(
    mapUndernoderToSelectable(praksissted.noder, 1),
  );

const mapMultipleToppnoderToSelectable = (
  praksisstedTopper: MinimalPraksisstedMedHierarkiDto[],
): PraksisstedForSelect[] =>
  praksisstedTopper.flatMap((praksissted) =>
    mapToppnodeToSelectable(praksissted),
  );

const flattenStedAndUndernoder = (
  praksissteder: MinimalPraksisstedMedHierarkiDto[],
): MinimalPraksisstedMedHierarkiDto[] =>
  praksissteder.reduce(
    (
      acc: MinimalPraksisstedMedHierarkiDto[],
      sted: MinimalPraksisstedMedHierarkiDto,
    ) => acc.concat(sted, ...flattenStedAndUndernoder(sted.noder.under)),
    [],
  );

const MIN_SEARCH_LENGTH = 3;
const SEARCH_TIMEOUT_IN_MS = 2000;

export const PraksisstedSelect: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { praksisstedToEdit } = usePraksisstedToEditContext();
  const { toppnode, setToppnode } = useSelectedStedContext();
  const [orgnr, setOrgnr] = useState<string>('');
  const [navn, setNavn] = useState<string>('');
  const [praksisstedValue, setPraksisstedValue] = useState<string>('');

  const { set: setSearchTimeout, clear: clearSearchTimeout } = useSetTimeout();

  const praksisstedToEditToppnode = useMemo(
    () => (praksisstedToEdit ? getToppnode(praksisstedToEdit) : undefined),
    [praksisstedToEdit],
  );

  const validOrgnr = useMemo(
    () =>
      validateOrgnr(
        praksisstedToEditToppnode ? praksisstedToEditToppnode.orgnr! : orgnr,
      ),
    [orgnr, praksisstedToEditToppnode],
  );

  const validRequest = useMemo(
    () => validOrgnr || navn.length >= MIN_SEARCH_LENGTH,
    [validOrgnr, navn],
  );

  const { praksisstedApi } = useContext(StudiestedApiContext);
  const { data, loading } = useLoader(() => {
    const params = {
      queryParams: validOrgnr
        ? {
            orgnr: parseInt(orgnr),
          }
        : { navn },
    };

    if (validRequest && !praksisstedToEditToppnode) {
      return praksisstedApi.praksisstederMedHierarkiGet(params);
    }

    if (praksisstedToEditToppnode) {
      return praksisstedApi.praksisstederMedHierarkiGet({
        queryParams: { orgnr: parseInt(praksisstedToEditToppnode.orgnr!) },
      });
    }

    return Promise.resolve([]);
  }, [orgnr, navn, validRequest, praksisstedToEditToppnode]);

  const muligePraksissteder = useMemo(
    () => data && mapMultipleToppnoderToSelectable(data),
    [data],
  );

  // If there is only one search result, and it has no undernoder, choose it automatically
  useEffect(() => {
    if (data && data.length === 1) {
      const onlyEntry = data[0];

      if (toppnode?.id !== onlyEntry.id && onlyEntry.noder.under.length === 0) {
        setToppnode(onlyEntry);
      }
    }
  }, [data, setToppnode, toppnode]);

  const flattenedSteder = useMemo(
    () => (data ? flattenStedAndUndernoder(data) : []),
    [data],
  );

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      modalStedField: formatMessage({
        id: praksisstedTranslations.modalStedField.id,
      }),
      modalStedFieldPlaceholder: formatMessage({
        id: praksisstedTranslations.modalStedFieldPlaceholder.id,
      }),
      modalStedFieldNoResults: formatMessage({
        id: praksisstedTranslations.modalStedFieldNoResults.id,
      }),
      modalStedChoose: formatMessage({
        id: praksisstedTranslations.modalStedChoose.id,
      }),
      modalStedChoosePlaceholder: formatMessage({
        id: praksisstedTranslations.modalStedChoosePlaceholder.id,
      }),
    }),
    [formatMessage],
  );

  const sendBackendRequest = (value: string) => {
    if (validateOrgnr(value)) {
      setOrgnr(value);
    } else if (!isStrictlyNumeric(value)) {
      setNavn(value);
    }
  };

  const onSearchChange = (value: string) => {
    clearSearchTimeout();

    setPraksisstedValue(value);
    setNavn('');
    setOrgnr('');

    setSearchTimeout(() => sendBackendRequest(value), SEARCH_TIMEOUT_IN_MS);
  };

  const onKeyDown = ({ key }: React.KeyboardEvent) => {
    if (key === 'Enter') {
      clearSearchTimeout();

      sendBackendRequest(praksisstedValue);
    }
  };

  const noPracticePlacesMatchesSearch = useMemo(
    () => validRequest && !loading && muligePraksissteder?.length === 0,
    [muligePraksissteder, loading, validRequest],
  );

  const lockedPraksisstedValue = useMemo(() => {
    if (praksisstedToEdit) {
      return praksisstedToEdit.noder.over
        ? getToppnode(praksisstedToEdit.noder.over).navn
        : praksisstedToEdit.navn;
    }
  }, [praksisstedToEdit]);

  const noMatchHelpText = useMemo(
    () =>
      noPracticePlacesMatchesSearch
        ? labels.modalStedFieldNoResults
        : undefined,
    [noPracticePlacesMatchesSearch, labels.modalStedFieldNoResults],
  );

  const onSelect = (
    _: object,
    newPraksissted: PraksisstedForSelect | undefined,
  ) => {
    const newSted = flattenedSteder.find(
      (praksissted) => praksissted.id === newPraksissted?.id,
    );

    setToppnode(newSted);
  };

  return (
    <>
      {praksisstedToEdit ? (
        <FakeLockedInput
          label={labels.modalStedField}
          value={lockedPraksisstedValue}
        />
      ) : (
        <TextInput
          htmlId={'praksissted-search'}
          onChange={onSearchChange}
          value={praksisstedValue}
          label={labels.modalStedField}
          placeholder={labels.modalStedFieldPlaceholder}
          onKeyDown={onKeyDown}
          helpText={noMatchHelpText}
          helpTextRole={'alert'}
          autoFocus
        />
      )}
      {loading ? (
        <Spinner height={TABLE_LOADER_HEIGHT} />
      ) : !validRequest ? (
        <></>
      ) : (
        muligePraksissteder &&
        muligePraksissteder.length > 0 && (
          <>
            <Autocomplete
              id={'praksissted-select'}
              label={labels.modalStedChoose}
              options={muligePraksissteder}
              onChange={onSelect}
              renderOption={(option) => (
                <IndentMuligPraksissted praksissted={option} />
              )}
              selectedOption={muligePraksissteder.find(
                (muligSted) => muligSted.id === toppnode?.id,
              )}
              placeholder={labels.modalStedChoosePlaceholder}
              containerClassname={css.searchField}
              required
            />
            {toppnode && (
              <>
                <ModalTable />
                {children}
              </>
            )}
          </>
        )
      )}
    </>
  );
};

export const PraksisstedViewMode: React.FC<
  React.PropsWithChildren<unknown>
> = ({ children }) => {
  const { praksisstedToEdit } = usePraksisstedToEditContext();

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      modalStedFieldView: formatMessage({
        id: praksisstedTranslations.modalStedFieldView.id,
      }),
      modalStedChosenView: formatMessage({
        id: praksisstedTranslations.modalStedChosenView.id,
      }),
    }),
    [formatMessage],
  );

  const lockedPraksisstedValue = useMemo(() => {
    if (praksisstedToEdit) {
      return praksisstedToEdit.noder.over
        ? getToppnode(praksisstedToEdit.noder.over).navn
        : praksisstedToEdit.navn;
    }
  }, [praksisstedToEdit]);

  return (
    <>
      <FakeLockedInput
        label={labels.modalStedFieldView}
        value={lockedPraksisstedValue}
        dataCy={'avtalested-topp'}
      />
      <FakeLockedInput
        label={labels.modalStedChosenView}
        value={praksisstedToEdit?.navn}
        containerClassName={css.searchField}
        dataCy={'avtalested'}
      />
      <ModalTable />
      {children}
    </>
  );
};

interface PraksisstedSigneringSectionProps {
  setSignatar: (
    praksisstedSignatar:
      | NewPraksisstedSignatar
      | NewPraksisstedSignatarDraft
      | undefined,
  ) => void;
  signatar?: NewPraksisstedSignatar | NewPraksisstedSignatarDraft;
  submit: (event: FormEvent) => void;
  formId: string;
}

enum PraksisstedSignatarActionType {
  SET_FORNAVN,
  SET_ETTERNAVN,
  SET_EPOST,
  SET_TLF,
}

const PraksisstedSignatarReducer: Reducer<
  NewPraksisstedSignatar | NewPraksisstedSignatarDraft,
  GenericActionWithPayload<PraksisstedSignatarActionType, string>
> = (state, action) => {
  switch (action.type) {
    case PraksisstedSignatarActionType.SET_FORNAVN:
      return { ...state, fornavn: action.payload };
    case PraksisstedSignatarActionType.SET_ETTERNAVN:
      return { ...state, etternavn: action.payload };
    case PraksisstedSignatarActionType.SET_EPOST:
      return { ...state, epost: action.payload };
    case PraksisstedSignatarActionType.SET_TLF:
      return { ...state, telefonnummer: action.payload };
  }

  return state;
};

export const PraksisstedSigneringSection: React.FC<
  React.PropsWithChildren<PraksisstedSigneringSectionProps>
> = ({ setSignatar, signatar, submit, formId }) => {
  const [state, dispatch] = useReducer(PraksisstedSignatarReducer, {
    fornavn: signatar?.fornavn || '',
    etternavn: signatar?.etternavn || '',
    epost: signatar?.epost || '',
    telefonnummer: signatar?.telefonnummer || '',
  });

  useEffect(() => {
    setSignatar(state);
  }, [state, setSignatar]);

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      signeringTitle: formatMessage({
        id: praksisstedTranslations.signeringTitle.id,
      }),
      signeringSubtitle: formatMessage({
        id: praksisstedTranslations.signeringSubtitle.id,
      }),
      fornavnLabel: formatMessage({
        id: praksisstedTranslations.fornavnLabel.id,
      }),
      etternavnLabel: formatMessage({
        id: praksisstedTranslations.etternavnLabel.id,
      }),
      epostLabel: formatMessage({
        id: praksisstedTranslations.epostLabel.id,
      }),
      tlfLabel: formatMessage({ id: praksisstedTranslations.tlfLabel.id }),
    }),
    [formatMessage],
  );

  return (
    <form id={formId} action={''} method={'post'} onSubmit={submit}>
      <PanelHeader
        title={labels.signeringTitle}
        subtitle={labels.signeringSubtitle}
      />
      <div className={css.inputFieldRow}>
        <TextInput
          containerClassName={css.textInput}
          htmlId={'fornavn-input'}
          label={labels.fornavnLabel}
          onChange={(value) =>
            dispatch({
              type: PraksisstedSignatarActionType.SET_FORNAVN,
              payload: value,
            })
          }
          value={state.fornavn}
        />
        <TextInput
          containerClassName={css.textInput}
          htmlId={'etternavn-input'}
          label={labels.etternavnLabel}
          onChange={(value) =>
            dispatch({
              type: PraksisstedSignatarActionType.SET_ETTERNAVN,
              payload: value,
            })
          }
          value={state.etternavn}
        />
      </div>
      <div className={css.inputFieldRow}>
        <TextInput
          containerClassName={css.textInput}
          htmlId={'epost-input'}
          label={labels.epostLabel}
          onChange={(value) =>
            dispatch({
              type: PraksisstedSignatarActionType.SET_EPOST,
              payload: value,
            })
          }
          value={state.epost}
        />
        <TextInput
          containerClassName={css.textInput}
          htmlId={'tlf-input'}
          label={labels.tlfLabel}
          onChange={(value) =>
            dispatch({
              type: PraksisstedSignatarActionType.SET_TLF,
              payload: value,
            })
          }
          value={state.telefonnummer}
        />
      </div>
    </form>
  );
};
